<template>
  <div
    data-pendo-ds="select_input"
    :data-testid="id"
    :style="{ width, 'max-width': '100%' }"
    @keyup.esc="closeDropdown({ focus: true })"
  >
    <slot name="label">
      <label
        v-if="label"
        :id="`${id}_select_input_label`"
        class="body-s-high"
      >
        <InputLabel
          :info="info"
          :label="label"
          :required="required"
          :style="{ 'margin-bottom': 'var(--space-1)' }"
        />
      </label>
    </slot>
    <div
      class="select-wrapper body-s-regular"
    >
      <BaseDropdown
        v-bind="formattedDropdownProps"
        @update:open="open = $event"
      >
        <template #trigger>
          <div
            ref="selectInput"
            :aria-expanded="open"
            :aria-labelledby="`${id}_select_input_label`"
            :aria-owns="`${id}_select_input_options`"
            class="select-trigger cr-br-small body-s-medium"
            :class="{ error, disabled, empty: isEmpty, open, 'show-styles': !$slots.trigger }"
            :data-testid="`${id}_trigger`"
            role="listbox"
            :tabindex="disabled ? -1 : 0"
            :value="value"
            @keydown.down.prevent
            @keydown.up.prevent
            @keyup.down="() => open ? moveToChildren() : open = true"
            @keyup.enter="open = !open"
            @keyup.up="() => open ? moveToLast() : open = true"
          >
            <slot
              :disabled="disabled"
              :error="error"
              name="trigger"
              :open="open"
            >
              <BaseCount
                v-if="!isEmpty && multiSelect"
                class="spc-mr-1"
                :count="value?.length"
                fillable
                :focusable="false"
                :hoverable="false"
              />
              {{ displayedValue }}
              <BaseIcon
                class="trigger-icon"
                :color="iconColor"
                font-size="var(--font-size-2)"
                icon="fa-chevron-down"
              />
            </slot>
          </div>
        </template>
        <template #content>
          <div
            :id="`${id}_select_input_options`"
            class="select-option-container"
            :style="{ maxHeight }"
            tabindex="-1"
          >
            <div
              v-if="searchable && (options.length >= minToSearch || overrideSearch)"
              class="search-input-container"
            >
              <input
                ref="searchInput"
                class="select-search-input"
                placeholder="Type to search"
                :value="searchValue"
                @input="updateSearch"
                @keyup.down="moveToChildren()"
                @keyup.up="moveToParent()"
              >
              <BaseButton
                v-if="searchValue.length > 0"
                emphasis="low"
                icon="fa-regular fa-close"
                theme="mono"
                @click.stop="clearSearch()"
              />
            </div>
            <div
              v-if="!filteredOptions.length"
              class="empty-options"
            >
              <slot name="empty-options">
                <div class="empty-options__message">
                  <template v-if="searchValue !== ''">
                    No results for '{{ searchValue }}'
                  </template>
                  <template v-else-if="loading">
                    <BaseIcon
                      class="base-button__icon"
                      icon="fas fa-spinner"
                      spin
                    />
                  </template>
                  <template v-else>
                    No options available
                  </template>
                </div>
              </slot>
            </div>
            <BaseButton
              v-else-if="(showClearButton || multiSelect) && showSelectAll"
              ref="controlButton"
              class="control-button"
              :disabled="allDisabled"
              emphasis="low"
              size="compact"
              type="button"
              @click="multiSelect ? toggleSelectAll() : selectOption({ value: '', label: '' })"
              @keydown.down.prevent
              @keydown.enter.prevent
              @keydown.up.prevent
              @keyup.down="moveToNext(-1)"
              @keyup.enter="multiSelect ? toggleSelectAll() : selectOption({ value: '', label: '' }, true)"
              @keyup.up="moveToParent"
            >
              {{ controlText }}
            </BaseButton>
            <div
              v-for="(option, idx) in filteredOptions"
              :key="`optiongroup${idx}`"
              class="select-option-group"
            >
              <SelectInputOption
                :ref="`option${getGlobalIndex(idx, -1)}`"
                :data-testid="`${id}_option_${option.value}`"
                :indeterminate="someChildrenSelected(option)"
                :multi-select="multiSelect"
                :option="option"
                :selected="isSelected(option)"
                :size="size"
                @go-next="moveToNext(getGlobalIndex(idx, -1))"
                @go-previous="moveToPrevious(getGlobalIndex(idx, -1))"
                @select="selectOption(option)"
                @select-focus="selectOption(option, true)"
              >
                <template #option>
                  <slot
                    name="option"
                    :option="option"
                  >
                    {{ option.label }}
                  </slot>
                </template>
                <template #option-decoration>
                  <slot
                    name="option-decoration"
                    :option="option"
                  />
                </template>
              </SelectInputOption>
              <div
                v-if="hasChildren(option)"
                class="select-option-group--children"
              >
                <SelectInputOption
                  v-for="(child, childIdx) in option.children"
                  :key="child.value"
                  :ref="`option${getGlobalIndex(idx, childIdx)}`"
                  :data-testid="`${id}_child_option_${child.value}`"
                  is-child
                  :multi-select="multiSelect"
                  :option="child"
                  :selected="isSelected(child)"
                  :size="size"
                  @go-next="moveToNext(getGlobalIndex(idx, childIdx))"
                  @go-previous="moveToPrevious(getGlobalIndex(idx, childIdx))"
                  @select="selectOption(child)"
                  @select-focus="selectOption(child, true)"
                >
                  <template #option>
                    <slot
                      name="option"
                      :option="child"
                    >
                      {{ child.label }}
                    </slot>
                  </template>
                  <template #option-decoration>
                    <slot
                      name="option-decoration"
                      :option="child"
                    />
                  </template>
                </SelectInputOption>
              </div>
            </div>
          </div>
          <slot name="new-option" />
        </template>
      </BaseDropdown>
    </div>
    <slot name="errors" />
  </div>
</template>

<script lang="ts">
import { type PropType } from 'vue'

import { library } from '@fortawesome/fontawesome-svg-core'
import { faChevronDown, faClose } from '@fortawesome/pro-solid-svg-icons'
import { faClose as farClose } from '@fortawesome/pro-regular-svg-icons'

library.add(faChevronDown, faClose, farClose)

import InputLabel from './InputLabel.vue'
import BaseDropdown from './BaseDropdown.vue'
import BaseButton from './BaseButton.vue'
import BaseIcon from './BaseIcon.vue'
import BaseCount from './BaseCount.vue'
import SelectInputOption from './SelectInputOption.vue'

export type InputOption = {
  label: string
  value: string
  disabled?: boolean
  children?: InputOptionWithParent[]
}

interface InputOptionWithParent extends InputOption {
  parent: string
}

interface InputOptionWithChildren extends InputOption {
  children: InputOptionWithParent[]
}

export default {
  name: 'SelectInput',
  components: {
    InputLabel,
    BaseDropdown,
    BaseIcon,
    BaseCount,
    BaseButton,
    SelectInputOption,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    options: {
      type: Array as PropType<InputOption[]>,
      required: true,
      validator: (options: InputOption[]) => {
        return options.every(option => 'label' in option && 'value' in option)
      },
    },
    value: {
      type: [String, Array] as PropType<string | string[]>,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    width: {
      type: String,
      default: '100%',
    },
    error: {
      type: Boolean,
      default: false,
    },
    info: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    dropdownProps: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String as PropType<'comfortable' | 'compact'>,
      default: 'comfortable',
      validator: (value: string) => ['comfortable', 'compact'].includes(value),
    },
    multiSelect: {
      type: Boolean,
      default: false,
    },
    selectedText: {
      type: String,
      default: 'Selected',
    },
    searchable: {
      type: Boolean,
      default: true,
    },
    minToSearch: {
      type: Number,
      default: 5,
    },
    overrideSearch: {
      type: Boolean,
      default: false,
    },
    clearText: {
      type: String,
      default: 'Clear selection',
    },
    maxHeight: {
      type: String,
      default: '225px',
    },
    showSelectAll: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['update:value', 'option-selected', 'update:search-value', 'dropdown-toggled', 'blur'],
  data() {
    return {
      open: false,
      dropdownWidth: '100%',
      searchValue: '',
    }
  },
  computed: {
    filteredOptions(): InputOption[] {
      if (!this.searchable || this.overrideSearch) {
        return this.options
      }

      return this.options.reduce((acc: InputOption[], option) => {
        if (this.hasChildren(option) && option.label.toLowerCase().includes(this.searchValue.toLowerCase())) {
          acc.push(option)
        } else if (this.hasChildren(option)) {
          const newChildren = option?.children?.filter(child => child.label.toLowerCase().includes(this.searchValue.toLowerCase()))

          if (newChildren?.length) {
            acc.push({
              ...option,
              children: newChildren,
            })
          }
        } else if (option.label?.toString()?.toLowerCase().includes(this.searchValue.toLowerCase())) {
          acc.push(option)
        }

        return acc
      }, [])
    },
    showClearButton(): boolean {
      return !this.multiSelect && !this.required && !this.isEmpty
    },
    controlText(): string {
      if (this.showClearButton) {
        return this.clearText
      }

      if (this.multiSelect) {
        return this.allSelected ? 'Deselect all' : 'Select all'
      }

      return ''
    },
    displayedValue(): string {
      if (this.multiSelect) {
        return Array.isArray(this.value) && this.value.length ? this.selectedText : this.placeholder || 'Select an option'
      }

      let selectedOption: InputOption | undefined

      for (const option of this.options) {
        if (option.value === this.value) {
          selectedOption = option
          break
        }

        if (this.hasChildren(option)) {
          const child = option.children.find(child => child.value === this.value)
          if (child) {
            selectedOption = child
            break
          }
        }
      }

      return selectedOption?.label ? selectedOption.label : this.placeholder || 'Select an option'
    },
    iconColor() {
      if (this.disabled) {
        return 'var(--font-color-secondary)'
      }

      return this.open ? 'var(--salt)' : 'var(--blueberry-1000)'
    },
    isEmpty(): boolean {
      return (this.value === '' || this.value === null) || (Array.isArray(this.value) && this.value.length === 0)
    },
    allSelected(): boolean {
      return this.multiSelect && this.filteredOptions.every(option => Array.isArray(this.value) && this.value.includes(option.value)
              || (this.hasChildren(option) && this.allChildrenSelected(option)) || option.disabled)
    },
    allDisabled(): boolean {
      return this.filteredOptions.every(option => option.disabled)
    },
    formattedDropdownProps() {
      return {
        animate: true,
        disabled: this.disabled,
        open: this.open,
        wrapperWidth: '100%',
        ...this.dropdownProps,
      }
    },
    // maps parentIdx:childIdx to a single set of indicies for easy navigation with $refs
    globalIndexes(): Map<string, number> {
      let count = 0
      const indexMap = this.filteredOptions.reduce((acc, option, idx) => {
        if (option.disabled) {
          return acc
        }

        if (!this.hasChildren(option) || (this.hasChildren(option) && this.multiSelect)) {
          acc.set(this.formatGlobalIndexKey(idx, -1), count)
          count++
        }

        if (this.hasChildren(option)) {
          option.children.forEach((child, childIdx) => {
            if (child.disabled) {
              return
            }

            acc.set(this.formatGlobalIndexKey(idx, childIdx), count)
            count++
          })
        }

        return acc
      }, new Map())

      return indexMap
    },
    lastOptionIndex() {
      return this.globalIndexes.size - 1
    },
  },
  watch: {
    open(open) {
      this.$emit('dropdown-toggled', open)

      if (!open && this.searchable && !this.overrideSearch) {
        this.searchValue = ''
      } else if (this.searchable) {
        setTimeout(() => {
          this.searchInput()?.focus()
        }, 100)
      }
    },
    searchValue(newValue) {
      this.$emit('update:search-value', newValue)
    },
  },
  methods: {
    // template $refs will not be reactive in computed properties
    searchInput() {
      return this.$refs.searchInput as HTMLInputElement | null
    },
    selectInput() {
      return this.$refs.selectInput as HTMLElement | null
    },
    updateSearch(event: Event) {
      const input = event.target as HTMLInputElement
      this.searchValue = input.value
    },
    clearSearch() {
      this.searchValue = ''
      this.searchInput()?.focus()
    },
    formatGlobalIndexKey(idx: number, childIdx: number): string {
      return `${idx}:${childIdx}`
    },
    getGlobalIndex(idx: number, childIdx: number) {
      return this.globalIndexes.get(this.formatGlobalIndexKey(idx, childIdx))
    },
    isSelected(option: InputOption | InputOptionWithParent, isChild = false): boolean {
      if (isChild || !this.hasChildren(option)) {
        return this.multiSelect && Array.isArray(this.value)
          ? this.value.includes(option.value) || (isChild && this.hasParent(option) && this.value.includes(option.parent))
          : this.value === option.value
      } else {
        return this.allChildrenSelected(option)
      }
    },
    hasParent(option: InputOption): option is InputOptionWithParent {
      return 'parent' in option
    },
    selectOption(option: InputOption, focus = false, isChild = false) {
      let newValue = this.value
      let newOption

      if (isChild && this.multiSelect && Array.isArray(this.value)) {
        newValue = this.value.includes(option.value)
          ? this.value.filter(v => v !== option.value)
          : [...this.value, option.value]
      }
      else if (this.multiSelect && Array.isArray(this.value)) {
        if (this.hasChildren(option)) {
          if (this.allChildrenSelected(option)) {
            newValue = this.value.filter(v => !option.children.some(child => child.value === v))
          } else {
            newValue = [...this.value, ...option.children.filter(child => !child.disabled).map(child => child.value)]
          }
        } else {
          newOption = option
          newValue = this.value.includes(option.value)
            ? this.value.filter(v => v !== option.value)
            : [...this.value, option.value]
        }
      } else {
        newOption = option
        newValue = option.value

        this.closeDropdown({ focus })
      }

      this.$emit('update:value', newValue)
      if (this.overrideSearch) { this.$emit('option-selected', newOption) }
    },
    allChildrenSelected(option: InputOption): boolean {
      return !this.hasChildren(option) || option.children.every(child => (Array.isArray(this.value) && this.value.includes(child.value)) || child.disabled)
    },
    someChildrenSelected(option: InputOption): boolean {
      return !this.allChildrenSelected(option) && (this.hasChildren(option) && option.children.some(child => Array.isArray(this.value) && this.value.includes(child.value)))
    },
    hasChildren(option: InputOption): option is InputOptionWithChildren {
      return Array.isArray(option.children) && option.children.length > 0
    },
    moveToChildren() {
      if (this.allDisabled) {
        return
      }

      const controlButton = this.$refs.controlButton as InstanceType<typeof BaseButton>

      if (controlButton) {
        controlButton.$el?.focus()
        return
      }

      this.moveToNext(-1)
    },
    moveToLast() {
      const lastOption = this.$refs[`option${this.lastOptionIndex}`] as InstanceType<typeof SelectInputOption>[]
      lastOption?.[0]?.$el?.focus()
    },
    moveToControls() {
      const controlButton = this.$refs.controlButton as InstanceType<typeof BaseButton>

      if (controlButton) {
        controlButton.$el?.focus()
        return true
      }

      return false
    },
    moveToParent() {
      this.selectInput()?.focus()
    },
    moveToNext(idx: number | undefined) {
      if (idx === undefined) {
        return
      }

      const nextIdx = idx + 1

      if (nextIdx > this.lastOptionIndex) {
        this.moveToParent()
        return
      }

      const nextOption = this.$refs[`option${nextIdx}`] as InstanceType<typeof SelectInputOption>[]
      nextOption?.[0]?.$el?.focus()
    },
    moveToPrevious(idx: number | undefined) {
      if (idx === undefined) {
        return
      }

      const previousIdx = idx - 1
      if (previousIdx === -1) {
        const didMove = this.moveToControls()

        if (didMove) {
          return
        }

        this.selectInput()?.focus()
        return
      }

      const previousOption = this.$refs[`option${previousIdx}`] as InstanceType<typeof SelectInputOption>[]
      previousOption?.[0]?.$el?.focus()
    },
    closeDropdown({ focus = false }) {
      this.open = false

      if (focus) {
        this.selectInput()?.focus()
      }
    },
    toggleSelectAll() {
      const filteredOutSelection = Array.isArray(this.value) && this.value?.filter((v) => !this.filteredOptions.some((opt) => opt.value === v ||
        (this.hasChildren(opt) && opt.children.some((child) => child.value === v)))) || []

      const newValue = this.allSelected
        ? this.filteredOptions.filter(option => option.disabled && Array.isArray(this.value) && this.value.includes(option.value)).map(option => this.hasChildren(option)
          ? option.children.filter(child => !child.disabled).map(child => child.value)
          : option.value).flat()
        : this.filteredOptions
          .filter(option => !option.disabled || (option.disabled && Array.isArray(this.value) && this.value.includes(option.value))).map(option => this.hasChildren(option)
          ? option.children.filter(child => !child.disabled).map(child => child.value)
          : option.value).flat()

      this.$emit('update:value', [...filteredOutSelection, ...newValue])
    },
  },
}
</script>

<style scoped lang="scss">
label {
  width: 100%;
  margin: 0;
  display: block;
}
.select-wrapper > * {
  position: relative;
  width: 100%;
}
.search-input-container {
  position: relative;

  button {
    position: absolute;
    right: var(--space-1);
    top: 50%;
    transform: translateY(-50%);
  }
}
.select-search-input {
  border: none;
  border-bottom: 1px solid var(--blueberry-200);
  padding: var(--space-2);
  width: 100%;

  &:focus {
    outline: none;
    border-bottom: 1px solid var(--blueberry-1000);
  }
}
.select-trigger.show-styles {
  width: 100%;
  position: relative;
  padding: .65rem;
  padding-left: var(--space-2);
  padding-right: var(--space-6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background: var(--salt);
  border: 1px solid var(--pepper-300);

  .trigger-icon {
    color: var(--blueberry-1000);
    padding: 5px;
    border-radius: var(--cr-br-small);
    position: absolute;
    right: var(--space-2);
    top: 50%;
    transform: translateY(-50%);
  }

  &.open {
    color: var(--blueberry-1000);
  }

  &.open .trigger-icon {
    transform: rotate(180deg) translateY(50%);
    background: var(--blueberry-1000);
  }

  &:hover:not(.open) .trigger-icon {
    background: var(--grape-jelly-50);
  }

  &:focus, &.open {
    outline: none;
    border: 1px solid var(--blueberry-1000);
  }

  &:focus-visible {
    outline: 2px solid var(--blueberry-200);
  }

  &.error {
    border: 1px solid var(--chicago-dog-red-1000);
    &:focus-visible {
      outline: 2px solid var(--blueberry-200);
    }
  }

  &.disabled {
    background: var(--pepper-200);
    color: var(--font-color-disabled);
    pointer-events: none;
  }

  &.disabled .trigger-icon {
    background: var(--pepper-300);
  }

  &.empty {
    color: var(--font-color-disabled);
    font-weight: var(--emphasis-regular);
  }
}

.select-trigger:not(.show-styles) {
  &:focus-visible {
    outline: 2px solid var(--blueberry-200);
  }
}

.select-option-container {
  width: 100%;
  padding: var(--space-2);
  padding-top: var(--space-1);
  max-height: 225px;
  overflow: auto;

  .control-button {
    padding-top: var(--space-2);
    padding-bottom: var(--space-1);
  }

  > * + * {
    margin-top: var(--space-1);
  }

  .empty-options {
    display: flex;
    justify-content: center;
    padding-bottom: var(--space-1);
    color: var(--pepper-600);
  }

  .empty-options__message {
    padding: var(--space-2);
  }

  .select-option-group {
    display: flex;
    flex-direction: column;
  }

}
</style>
